import { useState } from 'react';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';
import FilterChip from '../../../components/ui/FilterChip';
import Select from '../../../components/ui/Select';
import SiteRepairsModal from './SiteRepairsModal';
import SiteFormModal from './SiteFormModal';
import SiteImportModal from './SiteImportModal';
import SiteSummaryPanel from './SiteSummaryPanel';
import SiteTableDesktop from './SiteTableDesktop';
import SiteMobileCards from './SiteMobileCards';
import { useSites } from '../hooks/useSites';

function Sites() {
  const [showImport, setShowImport] = useState(false);
  const {
    navigate,
    isManageSitesContext,
    canManageSites,
    sites,
    sitePagination,
    isLoading,
    error,
    siteFilter,
    setSiteFilter,
    siteSortBy,
    setSiteSortBy,
    siteSortOrder,
    setSiteSortOrder,
    setSitePage,
    filteredSites,
    dueThisMonthIds,
    showUpcomingDivider,
    summaryCounts,
    assetsSummary,
    defectCountBySiteId,
    inProgressBySite,
    inProgressWarning,
    setHoveredSiteId,
    hoverEnterTimeoutRef,
    hoverLeaveTimeoutRef,
    hoveredSite,
    hoveredDefectMeta,
    hoveredSessions,
    hoveredAssetsSummary,
    showForm,
    setShowForm,
    editingSite,
    showMasterDateConfirm,
    setShowMasterDateConfirm,
    setPendingSubmit,
    isSubmitting,
    formData,
    setFormData,
    register,
    showRepairsModal,
    selectedRepairSite,
    selectedSiteRepairsPayload,
    selectedSiteRepairsLoading,
    selectedSiteRepairsError,
    resetForm,
    handleSubmit,
    confirmMasterDateChange,
    closeOutstandingRepairs,
    handleStartRepair,
    openEdit,
  } = useSites();

  if (isLoading) return <div className="page-loading">Loading...</div>;
  if (error) {
    const isNetworkError = error.code === 'ERR_NETWORK' || !error.response;
    return (
      <div className="page-error">
        {isNetworkError ? (
          <>
            <p><strong>Cannot connect to the server.</strong></p>
            <p>Make sure the backend is running: in the <code>backend</code> folder run <code>npm run dev</code> (port 3000).</p>
          </>
        ) : (
          <p>Failed to load sites.</p>
        )}
      </div>
    );
  }

  return (
    <div className="page page-sites">
      {isManageSitesContext ? (
        <header className="settings-tab-header settings-tab-header--split">
          <div>
            <h1 className="settings-tab-title">Manage sites</h1>
            <p className="text-muted settings-tab-description">
              Create and edit sites for your organisation.
            </p>
          </div>
          <div className="settings-tab-header-actions">
            {canManageSites && (
              <Button type="button" onClick={() => setShowImport(true)}>
                Import CSV
              </Button>
            )}
            <Button
              type="button"
              variant="primary"
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
            >
              Add Site
            </Button>
          </div>
        </header>
      ) : (
        <div className="page-header">
          <h1 className="page-title">Sites</h1>
          <div className="page-header-actions">
            <Button
              type="button"
              variant="primary"
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
            >
              Add Site
            </Button>
          </div>
        </div>
      )}

      <div className="site-filter-tabs" role="group" aria-label="Site filter">
        <FilterChip active={siteFilter === 'all'} onClick={() => setSiteFilter('all')}>
          All
        </FilterChip>
        <FilterChip active={siteFilter === 'due_this_month'} onClick={() => setSiteFilter('due_this_month')}>
          Due This Month
        </FilterChip>
        <FilterChip active={siteFilter === 'repairs_due'} onClick={() => setSiteFilter('repairs_due')}>
          Repairs Due
        </FilterChip>
      </div>

      <div className="filters">
        {inProgressWarning && (
          <p className="text-muted" style={{ width: '100%', marginBottom: '0.5rem' }}>
            In-progress session badges are temporarily unavailable.
          </p>
        )}
        <Select
          inline
          value={siteSortBy}
          onChange={(e) => { setSiteSortBy(e.target.value); setSitePage(1); }}
          aria-label="Sort sites by"
        >
          <option value="name">Sort by Name</option>
          <option value="next_test_due_date">Sort by Next Test Due Date</option>
        </Select>
        <Select
          inline
          value={siteSortOrder}
          onChange={(e) => { setSiteSortOrder(e.target.value); setSitePage(1); }}
          aria-label="Sort order"
        >
          <option value="asc">Ascending</option>
          <option value="desc">Descending</option>
        </Select>
      </div>

      <SiteRepairsModal
        isOpen={showRepairsModal}
        onClose={closeOutstandingRepairs}
        site={selectedRepairSite}
        payload={selectedSiteRepairsPayload}
        isLoading={selectedSiteRepairsLoading}
        isError={selectedSiteRepairsError}
        onStartRepair={handleStartRepair}
      />

      {isSubmitting && (
        <div className="modal-overlay site-update-loading-overlay" aria-live="polite" role="status">
          <div className="site-update-loading-content">
            <div className="site-update-loading-spinner" />
            <p>Updating site…</p>
          </div>
        </div>
      )}

      <Modal
        isOpen={showMasterDateConfirm}
        onClose={() => { if (!isSubmitting) { setShowMasterDateConfirm(false); setPendingSubmit(null); } }}
        title="Recalculate asset due dates?"
      >
        <p>Changing the Master Testing Date will recalculate the due dates for <strong>all assets</strong> on this site. Continue?</p>
        <div className="form-actions">
          <Button type="button" onClick={() => { setShowMasterDateConfirm(false); setPendingSubmit(null); }} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="button" variant="primary" onClick={confirmMasterDateChange} disabled={isSubmitting}>
            Yes, update date and recalculate
          </Button>
        </div>
      </Modal>

      <SiteFormModal
        isOpen={showForm && !showMasterDateConfirm}
        onClose={resetForm}
        onSubmit={handleSubmit}
        formData={formData}
        setFormData={setFormData}
        register={register}
        isSubmitting={isSubmitting}
        isEditing={!!editingSite}
        canManageSites={canManageSites}
      />

      {canManageSites && (
        <SiteImportModal isOpen={showImport} onClose={() => setShowImport(false)} />
      )}

      {/* Desktop: two-column layout (table + sticky preview panel on wide screens) */}
      <div className="sites-desktop-layout">
        <SiteTableDesktop
          filteredSites={filteredSites}
          inProgressBySite={inProgressBySite}
          defectCountBySiteId={defectCountBySiteId}
          dueThisMonthIds={dueThisMonthIds}
          showUpcomingDivider={showUpcomingDivider}
          hasSites={!!sites && sites.length > 0}
          onRowMouseEnter={(siteId) => {
            if (hoverLeaveTimeoutRef.current) window.clearTimeout(hoverLeaveTimeoutRef.current);
            if (hoverEnterTimeoutRef.current) window.clearTimeout(hoverEnterTimeoutRef.current);
            hoverEnterTimeoutRef.current = window.setTimeout(() => setHoveredSiteId(siteId), 75);
          }}
          onRowMouseLeave={() => {
            if (hoverEnterTimeoutRef.current) window.clearTimeout(hoverEnterTimeoutRef.current);
            if (hoverLeaveTimeoutRef.current) window.clearTimeout(hoverLeaveTimeoutRef.current);
            hoverLeaveTimeoutRef.current = window.setTimeout(() => setHoveredSiteId(null), 120);
          }}
          onRowClick={(siteId) => navigate(`/sites/${siteId}`)}
          onEdit={canManageSites ? openEdit : undefined}
        />

        <SiteSummaryPanel
          hoveredSite={hoveredSite}
          summaryCounts={summaryCounts}
          assetsSummary={assetsSummary}
          hoveredDefectMeta={hoveredDefectMeta}
          hoveredAssetsSummary={hoveredAssetsSummary}
          hoveredSessions={hoveredSessions}
        />
      </div>

      {/* Mobile: site cards (<768px) — whole card links to site details; address is text only */}
      <SiteMobileCards
        filteredSites={filteredSites}
        inProgressBySite={inProgressBySite}
        defectCountBySiteId={defectCountBySiteId}
        dueThisMonthIds={dueThisMonthIds}
        showUpcomingDivider={showUpcomingDivider}
        hasSites={!!sites && sites.length > 0}
        onEdit={canManageSites ? openEdit : undefined}
      />

      {sitePagination && sitePagination.totalPages > 1 && (
        <div className="pagination-controls">
          <Button
            type="button"
            size="sm"
            onClick={() => setSitePage((p) => Math.max(p - 1, 1))}
            disabled={sitePagination.currentPage <= 1}
          >
            Previous
          </Button>
          <span className="pagination-info">
            Page {sitePagination.currentPage} of {sitePagination.totalPages}
          </span>
          <Button
            type="button"
            size="sm"
            onClick={() => setSitePage((p) => p + 1)}
            disabled={!sitePagination.hasNextPage}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}

export default Sites;
